import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

export type EstadoSeguimiento = 'pendiente' | 'cumplida' | 'no_cumplida';

export interface ProgramacionAsignacion {
  id: string;
  programacionId: string;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  participanteId?: string | null;
  ministerioId?: string | null;
  liderId?: string | null;
  rol?: string | null;
  actividad?: string | null;
  observaciones?: string | null;
  estado?: EstadoSeguimiento | null;
  novedad?: string | null;
  participante?: { id: string; nombreCompleto: string } | null;
  ministerio?: { id: string; nombre: string } | null;
  lider?: { id: string; nombreCompleto: string } | null;
  programacion?: {
    id: string;
    fechaInicio: string;
    fechaFin: string;
    descripcion: string;
  } | null;
}

export interface ProgramacionSemanal {
  id: string;
  fechaInicio: string;
  fechaFin: string;
  descripcion: string;
  estado: boolean;
  creadoEn: string;
  actualizadoEn: string;
  asignaciones?: ProgramacionAsignacion[];
  totalAsignaciones?: number;
}

export interface PaginatedProgramaciones {
  data: ProgramacionSemanal[];
  total: number;
}

export interface CatalogosProgramacion {
  miembros: Array<{ id: string; nombreCompleto: string }>;
  ministerios: Array<{ id: string; nombre: string }>;
}

export interface AsignacionInput {
  fecha: string;
  horaInicio: string;
  horaFin: string;
  participanteId?: string;
  ministerioId?: string;
  liderId?: string;
  rol?: string;
  actividad?: string;
  observaciones?: string;
  estado?: string;
  novedad?: string;
}

export interface CreateProgramacionInput {
  fechaInicio: string;
  fechaFin: string;
  descripcion: string;
  asignaciones: AsignacionInput[];
}

class ProgramacionServiceClass {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async list(page = 1, limit = 10, search = ''): Promise<PaginatedProgramaciones> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.list}?${params}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Error al cargar las programaciones');
    const result = await response.json();
    return result.data ?? result;
  }

  async getById(id: string): Promise<ProgramacionSemanal> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.detail(id)}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Error al cargar la programación');
    const result = await response.json();
    return result.data ?? result;
  }

  async create(data: CreateProgramacionInput): Promise<ProgramacionSemanal> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.create}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.message || 'Error al crear la programación');
    }
    const result = await response.json();
    return result.data ?? result;
  }

  async update(id: string, data: CreateProgramacionInput): Promise<ProgramacionSemanal> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.update(id)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.message || 'Error al actualizar la programación');
    }
    const result = await response.json();
    return result.data ?? result;
  }

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.delete(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Error al eliminar la programación');
  }

  /**
   * Actualiza solo el seguimiento (estado/novedad) de una asignación.
   * Requiere permiso 'editar' de Programación Semanal.
   */
  async actualizarSeguimiento(
    asignacionId: string,
    data: { estado?: string; novedad?: string },
  ): Promise<ProgramacionAsignacion> {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.programacion.seguimiento(asignacionId)}`,
      {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.message || 'Error al actualizar el seguimiento');
    }
    const result = await response.json();
    return result.data ?? result;
  }

  async getCatalogos(): Promise<CatalogosProgramacion> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.catalogos}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Error al cargar los catálogos');
    const result = await response.json();
    return result.data ?? result;
  }

  /** Semana en curso (o próxima) con sus asignaciones. null si no hay ninguna. */
  async getActual(): Promise<ProgramacionSemanal | null> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.programacion.actual}`, {
      headers: this.getAuthHeaders(),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Error al cargar la programación actual');
    const result = await response.json();
    // Cuando no hay programación, el backend devuelve { data: null }
    if (result && typeof result === 'object' && 'data' in result) return result.data ?? null;
    return result ?? null;
  }

  /**
   * Asignaciones futuras de un ministerio. Requiere permiso 'ver' de Programación
   * Semanal o ser líder del ministerio; si no, el backend responde 403.
   */
  async getAsignacionesPorMinisterio(
    ministerioId: string,
  ): Promise<ProgramacionAsignacion[]> {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.programacion.asignacionesPorMinisterio(ministerioId)}`,
      { headers: this.getAuthHeaders() },
    );
    if (response.status === 403) throw new Error('forbidden');
    if (!response.ok) throw new Error('Error al cargar la programación del ministerio');
    const result = await response.json();
    return result.data ?? result;
  }
}

export const ProgramacionService = new ProgramacionServiceClass();
